import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Lightbulb } from "lucide-react";
import { useCreateTaxonomySuggestion, SuggestionType } from "@/hooks/useTaxonomySuggestions";

interface SuggestTaxonomyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: SuggestionType;
}

export function SuggestTaxonomyDialog({
  open,
  onOpenChange,
  type,
}: SuggestTaxonomyDialogProps) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const createSuggestion = useCreateTaxonomySuggestion();

  const typeLabel = type === "skill" ? "skill" : "industry";
  const typeLabelCapitalized = type === "skill" ? "Skill" : "Industry";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;

    try {
      await createSuggestion.mutateAsync({
        type,
        label: label.trim(),
        description: description.trim() || undefined,
      });
      // Reset form and close
      setLabel("");
      setDescription("");
      onOpenChange(false);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setLabel("");
      setDescription("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            Suggest a New {typeLabelCapitalized}
          </DialogTitle>
          <DialogDescription>
            Can't find what you're looking for? Suggest a new {typeLabel} to be
            added to the official list. Your suggestion will be reviewed by our
            team.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="suggestion-label">
              {typeLabelCapitalized} Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="suggestion-label"
              placeholder={
                type === "skill"
                  ? "e.g., Supply Chain Management"
                  : "e.g., Sustainable Agriculture"
              }
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={100}
              required
            />
            <p className="text-xs text-muted-foreground">
              Please check the existing list first to avoid duplicates.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="suggestion-description">
              Description (Optional)
            </Label>
            <Textarea
              id="suggestion-description"
              placeholder={`How would you define this ${typeLabel}?`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              A brief description helps reviewers understand your suggestion.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createSuggestion.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!label.trim() || createSuggestion.isPending}
            >
              {createSuggestion.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Suggestion"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
